import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { PortalApiService } from '../portal-api.service';
import { PortalAuthService } from '../portal-auth.service';
import { PortalPreviewService } from '../portal-preview.service';
import { portalCoverStyle } from '../shared/portal-cover';

@Component({
	selector: 'portal-dashboard',
	templateUrl: './dashboard.component.html',
	styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

	// The cover crop staff framed for each project card.
	coverStyle = portalCoverStyle;

	projects: any[] = [];
	loading = true;
	error = '';
	tab: 'all' | 'active' | 'completed' | 'cancelled' = 'active';

	// Set when the visitor asked for the list themselves, with the header's
	// switch-project breadcrumb. Read here in the constructor because the current
	// navigation is gone by the time ngOnInit runs.
	private switchRequested = false;

	constructor(
		private api: PortalApiService,
		private auth: PortalAuthService,
		private preview: PortalPreviewService,
		private router: Router
	) {
		this.switchRequested = !!this.router.getCurrentNavigation()?.extras?.state?.['switchProject'];
	}

	get activeProjects(): any[] {
		return this.projects.filter(p => p.status === 'active');
	}

	get completedProjects(): any[] {
		return this.projects.filter(p => p.status === 'completed');
	}

	get cancelledProjects(): any[] {
		return this.projects.filter(p => p.status === 'cancelled');
	}

	// Projects for the currently-selected tab.
	get filtered(): any[] {
		if (this.tab === 'all') return this.projects;
		if (this.tab === 'cancelled') return this.cancelledProjects;
		if (this.tab === 'completed') return this.completedProjects;
		return this.activeProjects;
	}

	ngOnInit(): void {
		this.api.project.list((res: any) => {
			this.projects = res?.projects || [];
			if (this.forwardToOnlyProject()) return;
			this.loading = false;
		}, (err: any) => {
			this.loading = false;
			if (err?.status === 401) { this.auth.goToLogin(); return; }
			this.error = err?.message || 'Could not load your projects.';
		});
	}

	/**
	 * Open a project.
	 *
	 * This list is also the preview's project picker, so the project clicked may not be the one the
	 * preview is scoped to. Re-scoping means asking WeQuote for a new session, because a preview
	 * cannot widen its own scope — see PortalPreviewService.switchTo().
	 */
	openProject(project: any): void {
		if (!project?.id) return;

		if (!this.preview.ready || project.id === this.preview.current?.project_id) {
			this.router.navigate(['/project', project.id]);
			return;
		}

		this.loading = true;
		this.preview.switchTo({ project_id: project.id, role_id: this.preview.current?.role_id ?? null }).then(ok => {
			this.loading = false;
			if (ok) this.router.navigate(['/project', project.id]);
			else this.error = this.preview.failureMessage || 'Could not open that project.';
		});
	}

	/**
	 * A customer with nothing to choose between here is sent straight into their
	 * project: a single active project, or - where none are active - a single
	 * project full stop. Anything they aren't forwarded to stays one press of the
	 * header's switch-project breadcrumb away.
	 *
	 * `replaceUrl` keeps the list out of the history, so Back from the project
	 * doesn't return here only to be forwarded again. Returns whether it forwarded,
	 * so the caller can leave the loader up rather than flashing the single card.
	 */
	private forwardToOnlyProject(): boolean {
		if (this.switchRequested) return false;

		// A preview lists every portal-enabled project, so "only one to choose from" never applies.
		// What decides it is whether the preview was opened on a project: if it was, go there; if it
		// wasn't, this list is the whole point of being here.
		if (this.preview.ready) {
			if (!this.preview.projectChosen) return false;

			const id = this.preview.current?.project_id;
			if (!id) return false;

			this.router.navigate(['/project', id], { replaceUrl: true }).catch(() => this.loading = false);
			return true;
		}

		const only = this.activeProjects.length === 1
			? this.activeProjects[0]
			: (this.projects.length === 1 ? this.projects[0] : null);
		if (!only?.id) return false;

		this.router.navigate(['/project', only.id], { replaceUrl: true }).catch(() => this.loading = false);
		return true;
	}
}
